<template>
    <div>
        <kendo-multiselect
            :ref="'multiNomDropdown' + _uid"
            :id="'multiNomDropdown' + _uid"
            v-if="loaded"
            v-model="selectedValues"
            :data-source="items"
            :data-text-field="isEnVersion ? 'textEn' : 'text'"
            :data-value-field="'id'"
            :filter="filterPattern"
            :optionLabel="$t('choose')"
            @change="inputChanged"
            :name="'dropdown' + _uid"
            :data-vv-as="fieldName"
            :no-data-template="nomDropdownSettings.showNoDataTemplate ? noDataTemplate : ''"
            :footer-template="nomDropdownSettings.showFooterTemplate ? footerTemplate : ''"
            :disabled="details"
            :height="height"
            v-validate="model.isRequired ? 'required' : ''"
            style="width: 100%;"
        >
        </kendo-multiselect>

        <span
            class="text-danger col-12"
            v-if="errors.has('dropdown' + _uid)"
        >{{ errors.first('dropdown' + _uid) }}</span>

        <vs-prompt
            :title="$t('registers.nomenclatures.add')"
            :active.sync="showNomCreate"
            :buttons-hidden="true"
            @cancel="cancelPrompt"
            @close="cancelPrompt"
            color="primary"
        >
            <div>
                <NomenclatureEditCard
                    :nomSort="nomParentSort"
                    :nomName="nomParentType"
                    :create="true"
                    :key="nomParentSort"
                    @nomEditCancel="nomEditCancel"
                    @nomEditSave="nomEditSave"
                ></NomenclatureEditCard>
                <div class="clearfix"></div>
            </div>
        </vs-prompt>

        <DropdownSettings v-if="loaded"
            :filterPattern.sync="filterPattern"
            :show.sync="showSettings">
        </DropdownSettings>
    </div>
</template>

<script>
import Vue from "vue";
import NomenclatureEditCard from "../dropdown/NomenclatureEditCard";
import NodataTemplate from "../../components/grid/NomenclatureDropdownNodataTemplate";
import FooterTemplate from "../../components/grid/NomenclatureDropdownFooterTemplate";
import DropdownSettings from "../../components/common/DropdownSettings";
import { EditorComponentModel } from "../../models/models";
import { mapGetters } from "vuex";

export default {
    props: {
        value: [String, Number],
        type: String,
        // Името което показва vee-validate
        fieldName: String,
        model: EditorComponentModel,
        isNew: {
            type: Boolean,
            default: false,
        },
        details: {
            type: Boolean,
            default: false,
        },
        height: {
            type: Number,
            default: 400, // 200 - Default value for Kendo DropdownList
        },
    },
    components: {
        NomenclatureEditCard,
        DropdownSettings
    },
    data() {
        return {
            items: [],
            selectedValues: [],
            loaded: false,
            showNomCreate: false,
            nomParentSort: 0,
            nomParentType: this.type,
            showSettings: false,
            filterPattern: '',
        };
    },
    methods: {
        init() {
            const url = `/api/search/GetNomenclatureItemsById?id=${this.model.dropdownId}&isCity=${this.model.dropdownIsCity}&selected=${this.value}`;
            this.$http
                .get(url)
                .then((result) => {
                    this.items = result.data;
                    this.loaded = true;

                    if (
                        this.model.fieldValue.textValue
                            .toString()
                            .indexOf("|") !== -1
                    ) {
                        const parsedValues = this.model.fieldValue.textValue
                            .toString()
                            .split("|");
                        parsedValues.forEach((value) => {
                            this.selectedValues.push(parseInt(value));
                        });
                    } else {
                        if (!isNaN(this.model.fieldValue.textValue)) {
                            this.selectedValues = parseInt(
                                this.model.fieldValue.textValue
                            );
                        }
                    }

                    if (
                        !this.selectedValues &&
                        this.model.textDefaultValue &&
                        this.isNew
                    ) {
                        const parsedValues = this.model.textDefaultValue
                            .toString()
                            .split("|");
                        parsedValues.forEach((value) => {
                            this.selectedValues.push(parseInt(value));
                        });
                    }

                    this.populateStringValueById(this.selectedValues);
                })
                .catch((error) => {
                    this.$vs.notify({
                        title: this.$t(
                            "request.response.status." +
                                this.$helpers.errorStatusCode(error)
                        ),
                        text: this.$helpers.errorText(error),
                        color: "danger",
                        time: this.$store.getters.notificationDuration,
                    });
                })
                .then(() => {
                    this.loading = false;
                });
        },
        inputChanged(event) {
            let valueToEmit = event.sender.value().join("|");
            this.model.fieldValue.textValue = valueToEmit;
            this.$emit("input", valueToEmit);
            this.populateStringValueById(event.sender.value());
        },
        populateStringValueById(ids) {
            if (Array.isArray(ids) === false) {
                return;
            }

            let stringValues = [];
            let vm = this;
            if (ids) {
                ids.forEach((id) => {
                    const val = vm.items.filter((item) => item.id === id);
                    if (val && val.length > 0) {
                        stringValues.push(val[0].text);
                    }
                });
            }
            if (stringValues.length > 0) {
                this.model.fieldValue.stringValue = stringValues.join(",");
            }
        },
        templateBtns(e) {
            return [
                this.hasCreateRights
                    ? {
                          name: "addNomenclature",
                          text: this.$t("buttons.add.btnText"),
                          tooltip: this.$t("buttons.add.btnText"),
                          class: "btn btn-secondary",
                          icon: "far fa-plus-square",
                          show: true,
                          clickHandler: this.addNomenclature,
                      }
                    : {},
            ];
        },
        noDataTemplate: function (e) {
            return this.model.dropdownIsCity
                ? {}
                : {
                      template: Vue.component(
                          NodataTemplate.name,
                          NodataTemplate
                      ),
                      templateArgs: {
                          ...e,
                          noDataFound: this.$t("dropdownList.noDataFound"),
                          createQuestion: this.$t(
                              "dropdownList.createQuestion"
                          ),
                          btns: this.templateBtns(e),
                      },
                  };
        },
        footerTemplate: function (e) {
            return this.model.dropdownIsCity
                ? {}
                : {
                      template: Vue.component(
                          FooterTemplate.name,
                          FooterTemplate
                      ),
                      templateArgs: {
                          ...e,
                          btns: [...this.templateBtns(e),
                            {
                                name: "nomenclatureMultiDropdownSettings",
                                text: this.$t("buttons.settings.btnText"),
                                tooltip: this.$t("buttons.settings.btnText"),
                                class: "btn btn-secondary",
                                icon: "fas fa-cog",
                                show: this.nomDropdownSettings.showSettings,
                                clickHandler: () => {
                                    this.showSettings = true;
                                },
                            }
                          ],
                      },
                  };
        },
        addNomenclature(args) {
            if (!this.hasCreateRights) {
                // Todo: show message
                return;
            }

            this.showNewNomenclatureModal(args);
        },
        showNewNomenclatureModal() {
            this.showNomCreate = true;
        },
        clearFilterAndText() {
            var dropdown = this.$refs["multiNomDropdown" + this._uid].kendoWidget();
            if (dropdown) {
                dropdown.dataSource.filter({});
                if (dropdown.filterInput && dropdown.filterInput.length > 0) {
                    dropdown.filterInput[0].value = "";
                }
            }
        },
        nomEditCancel() {
            this.clearFilterAndText();
            this.showNomCreate = false;
        },
        nomEditSave() {
            this.clearFilterAndText();
            this.showNomCreate = false;
            this.loadItems();
        },
        cancelPrompt() {
            this.clearFilterAndText();
            this.showNomCreate = false;
        },
        selectContextMenu(e) {
            if (btnId === 'settings') {
                this.showSettings = true;
            }
        },
    },
    computed: {
        ...mapGetters(['isEnVersion', 'claims', 'nomDropdownSettings']),
        hasCreateRights() {
            return this.claims.nomenclature.create;
        },
    },
    watch: {
        value: function (newVal, oldVal) {
            this.model.fieldValue.textValue = newVal;
        },
    },
    mounted() {
        this.filterPattern = this.nomDropdownSettings.defaultFilterPattern;
        this.init();
    },
};
</script>
